import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CreateSlideConfig,
  GenerationPhase,
  GenerationRun,
  SlideGenerator,
  SlidePlan,
  TemplatePlan,
  TemplateSlideGenerator,
} from "./types";

interface GenState {
  phase: GenerationPhase;
  plans: SlidePlan[];
  current: number;
  total: number;
  error: string | null;
}

const INITIAL: GenState = { phase: "idle", plans: [], current: 0, total: 0, error: null };

const NOT_AVAILABLE_MESSAGE =
  "Slide generation isn't available — sign in and ensure the backend is configured.";

/**
 * Owns generation state for the Create-slides flow and adapts the generator
 * callbacks into React state. Pass the real backend generators; if one is
 * null (not signed in / backend not configured), the matching start function
 * surfaces a clear error instead of running.
 *
 * Both design paths share this hook - and therefore one progress view - even
 * though they run through different endpoints: `start` drives the AI-freehand
 * SSE generator, `startTemplates` the templates one (which needs the approved
 * layout plan rather than the raw wizard config).
 */
export function useCreateSlideGenerator(
  generator: SlideGenerator | null,
  templateGenerator: TemplateSlideGenerator | null = null,
) {
  const runRef = useRef<GenerationRun | null>(null);
  const [state, setState] = useState<GenState>(INITIAL);

  // Cancel any in-flight generation when the consuming component unmounts (e.g.
  // the user leaves the Create-slides tab mid-stream) — otherwise the async
  // stream keeps running, setting state on an unmounted component and inserting
  // slides into the deck after the UI is gone.
  useEffect(() => () => runRef.current?.cancel(), []);

  const updateSlide = useCallback(
    (index: number, status: SlidePlan["status"], detail: SlidePlan["detail"]) =>
      setState((s) => ({
        ...s,
        plans: s.plans.map((p) => (p.index === index ? { ...p, status, detail } : p)),
      })),
    [],
  );
  /** Callbacks are identical for both generators, so the adapter is shared. */
  const callbacks = useCallback(
    () => ({
      onPlan: (plans: SlidePlan[]) => setState((s) => ({ ...s, plans, total: plans.length })),
      onSlideUpdate: updateSlide,
      onProgress: (current: number, total: number) => setState((s) => ({ ...s, current, total })),
      onPhase: (phase: GenerationPhase) => setState((s) => ({ ...s, phase })),
      onError: (message: string) => setState((s) => ({ ...s, error: message })),
    }),
    [updateSlide],
  );

  const start = useCallback(
    (config: CreateSlideConfig) => {
      if (!generator) {
        setState({ ...INITIAL, phase: "error", error: NOT_AVAILABLE_MESSAGE });
        return;
      }
      setState({ ...INITIAL, phase: "outlining" });
      runRef.current = generator.generate(config, callbacks());
    },
    [generator, callbacks],
  );

  const startTemplates = useCallback(
    (plan: TemplatePlan) => {
      if (!templateGenerator) {
        setState({ ...INITIAL, phase: "error", error: NOT_AVAILABLE_MESSAGE });
        return;
      }
      setState({ ...INITIAL, phase: "outlining" });
      runRef.current = templateGenerator.generate(plan, callbacks());
    },
    [templateGenerator, callbacks],
  );

  const cancel = useCallback(() => runRef.current?.cancel(), []);
  const reset = useCallback(() => {
    runRef.current?.cancel();
    setState(INITIAL);
  }, []);

  return { ...state, start, startTemplates, cancel, reset };
}
